// Thin wrapper around amazon-cognito-identity-js for the sign-in flow
// plus a raw Cognito refresh call used by AuthBase.getFreshTokens.
//
// The Service framework (scripts/plunk/service) already handles:
//   - storing tokens in localStorage('plunkOAuthTokens')
//   - attaching Authorization: Bearer to every request
//   - retrying 401s by calling getFreshTokens(refreshToken) and re-firing
// This module only provides the auth flow and refresh implementation;
// AuthBase wires them in.

import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import config from './cognito-config';
const pool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.clientId
});
export function getPool() {
  return pool;
}

// Sign in with email/password using USER_PASSWORD_AUTH. Returns the three
// JWTs on success. Caller is responsible for persisting them via Service
// framework's `tokens` setter.
export function signIn(email, password) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: pool
    });
    const auth = new AuthenticationDetails({
      Username: email,
      Password: password
    });
    user.setAuthenticationFlowType('USER_PASSWORD_AUTH');
    user.authenticateUser(auth, {
      onSuccess: session => resolve({
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        expiresIn: session.getIdToken().getExpiration() // unix seconds
      }),
      onFailure: err => reject(err)
    });
  });
}

// Cognito sign-out: clears the SDK's in-memory user. Persistent tokens
// (refreshToken in localStorage) are cleared separately via
// AuthBase.removeTokens().
export function signOut() {
  const user = pool.getCurrentUser();
  if (user) user.signOut();
}

// Raw refresh — used by AuthBase.getFreshTokens. We hit Cognito's IDP
// endpoint directly with REFRESH_TOKEN_AUTH; this avoids needing the SDK
// to reconstruct a CognitoUser session just to refresh.
export async function refreshTokens(refreshToken) {
  const res = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
    },
    body: JSON.stringify({
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: config.clientId,
      AuthParameters: {
        REFRESH_TOKEN: refreshToken
      }
    })
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(`Cognito refresh failed: ${res.status} ${text}`);
    err.statusCode = res.status;
    throw err;
  }
  const body = await res.json();
  const result = body.AuthenticationResult || {};
  return {
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    // Cognito doesn't return a new refresh token on REFRESH_TOKEN_AUTH —
    // the original keeps working for the full 180-day TTL. Caller
    // should preserve the existing refreshToken in storage.
    refreshToken: refreshToken,
    expiresIn: result.ExpiresIn
  };
}

// Parse JWT claims (middle segment, base64-url decode). Pure function —
// no signature verification (server does that).
export function parseClaims(idToken) {
  if (!idToken) return null;
  try {
    const parts = idToken.split('.');
    if (parts.length !== 3) return null;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4) payload += '=';
    const json = decodeURIComponent(atob(payload).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

// Per the doc: custom attribute booleans are stored as the strings
// "true"/"false". Coerce here so consumers can use them as real bools.
export function boolClaim(claims, key) {
  if (!claims) return false;
  const v = claims[key];
  if (v === true || v === 'true') return true;
  return false;
}

// `custom:product` is a JSON string. Returns the parsed object or null.
export function productClaim(claims) {
  if (!claims) return null;
  const raw = claims['custom:product'] || claims.product;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}
export default {
  getPool,
  signIn,
  signOut,
  refreshTokens,
  parseClaims,
  boolClaim,
  productClaim
};